/**
 * CircuitLineageChain — vertical, mono-spaced rendering of a Circuit's
 * `parent_circuit_id` chain.
 *
 * Replaces the placeholder string "← branched from abc12345…" with a
 * navigable chain that tells you *what* the parents are, not just
 * *that* they exist. Reads from `GET /circuits/{cid}/lineage` (oldest
 * first — root → input), so the input Circuit is always the last row
 * and gets the "(this)" marker.
 *
 * Render only when the input Circuit has a parent — root circuits
 * produce a single-row chain that adds no information. The parent
 * (CircuitCard expanded view) does that gate.
 */
import { CircuitKindBadge } from "@features/projects/components/CircuitKindBadge";
import { VersionBadge } from "@features/versions/components/VersionBadge";
import type { CircuitDTO } from "@models/circuit-ir";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCircuitLineage } from "../hooks/useCircuitLifecycle";

interface CircuitLineageChainProps {
  circuit: CircuitDTO;
}

export function CircuitLineageChain({ circuit }: CircuitLineageChainProps) {
  const { data: chain, isLoading, error } = useCircuitLineage(circuit.id);

  if (isLoading) {
    return (
      <Box sx={{ py: 1, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={14} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 1, py: 0.5 }}>
        Could not load lineage: {(error as Error).message}
      </Alert>
    );
  }

  // No chain or chain of one (root circuit) — nothing meaningful to show.
  // The parent gates on parent_circuit_id but defend against API drift.
  if (!chain || chain.length < 2) return null;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={(t) => ({
          color: t.palette.editorial.main,
          fontSize: 10,
          letterSpacing: "0.16em",
          mb: 0.5,
          display: "block",
        })}
      >
        Lineage · {chain.length}
      </Typography>
      <Stack
        spacing={0}
        sx={(t) => ({
          fontFamily: t.fonts.mono,
          fontSize: 12,
          color: "text.secondary",
        })}
      >
        {chain.map((c, idx) => {
          const isInput = idx === chain.length - 1;
          const isFirst = idx === 0;
          return (
            <LineageRow
              key={c.id}
              circuit={c}
              depth={idx}
              isInput={isInput}
              isFirst={isFirst}
            />
          );
        })}
      </Stack>
    </Box>
  );
}

interface LineageRowProps {
  circuit: CircuitDTO;
  depth: number;
  isFirst: boolean;
  isInput: boolean;
}

function LineageRow({ circuit, depth, isFirst, isInput }: LineageRowProps) {
  // Indent grows linearly so deeper descendants visibly nest. Cap depth
  // at 6 to keep wide boards readable when someone forks 8 levels deep.
  const indent = Math.min(depth, 6) * 14;
  const versionLabel =
    circuit.current_version_semver?.trim() ||
    (circuit.current_version_number != null
      ? `v${circuit.current_version_number}`
      : null);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={(t) => ({
        py: 0.5,
        pl: `${indent}px`,
        borderLeft: isInput
          ? `2px solid ${alpha(t.palette.primary.main, 0.6)}`
          : "2px solid transparent",
        bgcolor: isInput ? "accent.softer" : "transparent",
        borderRadius: 0.5,
      })}
    >
      {!isFirst && (
        <Box
          component="span"
          sx={(t) => ({
            color: alpha(t.palette.editorial.main, 0.7),
            fontFamily: t.fonts.mono,
            fontSize: 12,
            lineHeight: 1,
          })}
        >
          ↳
        </Box>
      )}
      <CircuitKindBadge kind={circuit.kind} />
      <Box
        component="span"
        sx={(t) => ({
          color: isInput ? "text.primary" : "text.secondary",
          fontWeight: isInput ? 600 : 400,
          fontFamily: t.fonts.mono,
          fontSize: 12,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
          minWidth: 0,
        })}
      >
        {circuit.name}
      </Box>
      {versionLabel && (
        <VersionBadge
          versionNumber={circuit.current_version_number}
          semverLabel={circuit.current_version_semver}
          isCurrent={
            circuit.status === "active" || circuit.status === "released"
          }
        />
      )}
      {isInput && (
        <Box
          component="span"
          sx={(t) => ({
            color: t.palette.editorial.main,
            fontSize: 10,
            letterSpacing: "0.14em",
            fontFamily: t.fonts.mono,
            ml: 0.5,
          })}
        >
          (this)
        </Box>
      )}
    </Stack>
  );
}
